// src/components/QuickItemModal.tsx
import React, { useState } from 'react';
import { 
  X, 
  Wrench, 
  Package, 
  Layers, 
  Plus, 
  Loader2, 
  DollarSign, 
  Tag, 
  Boxes, 
  Calendar, 
  ShieldAlert, 
  CheckCircle2,
  Sparkles,
  Info,
  Clock
} from 'lucide-react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

export type QuickItemType = 'service' | 'product' | 'bundle';

export interface CreatedQuickItem {
  itemType: QuickItemType;
  _id: string;
  name: string;
  basePrice: number;
}

interface QuickItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: QuickItemType;
  onItemCreated: (item: CreatedQuickItem) => void;
}

// Enums específicos para Serviços
const SERVICE_UNITS = [
  { value: 'unit', label: 'Por Unidade / Serviço' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'daily', label: 'Diário' },
  { value: 'yearly', label: 'Anual' },
  { value: 'set', label: 'Conjunto / Pacote' },
  { value: 'box', label: 'Caixa' },
];

// Enums específicos para Produtos (Discriminators / Categorias oficiais)
const PRODUCT_CATEGORIES = [
  'Geral',
  'Restaurante',
  'Construção',
  'Gráfica',
  'Utensílios',
  'Perfumaria',
  'Calçados',
  'Cabelos',
  'Bijuteria',
  'Plantas',
  'Acessórios Auto',
  'Veículos',
  'Informática',
  'Talho',
  'Microcrédito'
];

const PRODUCT_UNITS = [
  { value: 'unid', label: 'Unidade (unid)' },
  { value: 'kg', label: 'Quilograma (kg)' },
  { value: 'g', label: 'Grama (g)' },
  { value: 'litro', label: 'Litro (L)' },
  { value: 'dose', label: 'Dose' },
  { value: 'm2', label: 'Metro quadrado (m²)' },
  { value: 'pack', label: 'Pacote (pack)' },
  { value: 'cx', label: 'Caixa (cx)' },
  { value: 'par', label: 'Par' },
];

const BILLING_CYCLES = [
  { value: 'Mensal', label: 'Mensal' },
  { value: 'Trimestral', label: 'Trimestral' },
  { value: 'Semestral', label: 'Semestral' },
  { value: 'Anual', label: 'Anual' },
  { value: 'Custom', label: 'Personalizado' },
];

export const QuickItemModal: React.FC<QuickItemModalProps> = ({
  isOpen,
  onClose,
  defaultType = 'service',
  onItemCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [itemType, setItemType] = useState<QuickItemType>(defaultType);

  // 1. Estado para Serviço
  const [serviceForm, setServiceForm] = useState({
    name: '',
    basePrice: 0,
    unit: 'unit',
    allowedInstallments: 3,
    penaltyPercentagePerInstallment: 2,
    targetAudience: '',
    description: '',
    showPrice: true,
    minPrice: 0,
    maxPrice: 0,
  });

  // 2. Estado para Produto
  const [productForm, setProductForm] = useState({
    name: '',
    category: 'Geral',
    basePrice: 0,
    costPrice: 0,
    stockQuantity: 10,
    minStockLevel: 5,
    unit: 'unid',
    sku: '',
    shortDescription: '',
  });

  // 3. Estado para Combo / Subscrição
  const [bundleForm, setBundleForm] = useState({
    name: '',
    type: 'Combo' as 'Combo' | 'Subscription',
    price: 0,
    originalPrice: 0,
    discountPercentage: 0,
    billingCycle: 'Mensal',
    billingPricePerCycle: 0,
    commitmentMonths: 1,
    extraBenefitsText: '',
    description: '',
  });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      let createdItem: any = null;
      let finalPrice = 0;
      let finalName = '';

      if (itemType === 'service') {
        if (!serviceForm.name.trim()) {
          toast.error('O nome do serviço é obrigatório');
          return;
        }

        const payload = {
          name: serviceForm.name.trim(),
          basePrice: Number(serviceForm.basePrice) || 0,
          unit: serviceForm.unit || 'unit',
          allowedInstallments: Number(serviceForm.allowedInstallments) || 3,
          penaltyPercentagePerInstallment: Number(serviceForm.penaltyPercentagePerInstallment) || 2,
          targetAudience: serviceForm.targetAudience.trim() || undefined,
          description: serviceForm.description.trim() || serviceForm.name.trim(),
          showPrice: serviceForm.showPrice,
          minPrice: Number(serviceForm.minPrice) || 0,
          maxPrice: Number(serviceForm.maxPrice) || 0,
          isActive: true,
        };

        createdItem = await api.services.create(payload);
        finalName = createdItem.name || serviceForm.name.trim();
        finalPrice = Number(serviceForm.basePrice) || createdItem.basePrice || 0;

      } else if (itemType === 'product') {
        if (!productForm.name.trim()) {
          toast.error('O nome do produto é obrigatório');
          return;
        }
        if (!productForm.category) {
          toast.error('Selecione uma categoria válida para o produto');
          return;
        }

        const payload = {
          name: productForm.name.trim(),
          category: productForm.category,
          basePrice: Number(productForm.basePrice) || 0,
          costPrice: Number(productForm.costPrice) || 0,
          stockQuantity: Number(productForm.stockQuantity) || 0,
          minStockLevel: Number(productForm.minStockLevel) || 5,
          unit: productForm.unit || 'unid',
          sku: productForm.sku.trim() || undefined,
          shortDescription: (productForm.shortDescription.trim() || productForm.name.trim()).slice(0, 160),
          isActive: true,
        };

        createdItem = await api.products.create(payload);
        finalName = createdItem.name || productForm.name.trim();
        finalPrice = Number(productForm.basePrice) || createdItem.basePrice || 0;

      } else if (itemType === 'bundle') {
        if (!bundleForm.name.trim()) {
          toast.error('O nome do combo/subscrição é obrigatório');
          return;
        }

        const isSub = bundleForm.type === 'Subscription';
        const benefitsArray = bundleForm.extraBenefitsText
          ? bundleForm.extraBenefitsText.split('\n').map(b => b.trim()).filter(Boolean)
          : [];

        const payload = {
          name: bundleForm.name.trim(),
          type: bundleForm.type,
          price: isSub ? (Number(bundleForm.billingPricePerCycle) || 0) : (Number(bundleForm.price) || 0),
          originalPrice: Number(bundleForm.originalPrice) || undefined,
          discountPercentage: Number(bundleForm.discountPercentage) || 0,
          billingCycle: isSub ? bundleForm.billingCycle : 'N/A',
          billingPricePerCycle: isSub ? (Number(bundleForm.billingPricePerCycle) || 0) : undefined,
          commitmentMonths: isSub ? (Number(bundleForm.commitmentMonths) || 1) : undefined,
          extraBenefits: benefitsArray.length ? benefitsArray : undefined,
          description: bundleForm.description.trim() || bundleForm.name.trim(),
          items: [],
          isActive: true,
        };

        createdItem = await api.bundles.create(payload);
        finalName = createdItem.name || bundleForm.name.trim();
        finalPrice = isSub 
          ? (Number(bundleForm.billingPricePerCycle) || createdItem.billingPricePerCycle || createdItem.price || 0)
          : (Number(bundleForm.price) || createdItem.price || 0);
      }

      toast.success(`${itemType === 'service' ? 'Serviço' : itemType === 'product' ? 'Produto' : bundleForm.type === 'Subscription' ? 'Subscrição' : 'Combo'} criado com sucesso!`);

      const result: CreatedQuickItem = {
        itemType,
        _id: createdItem?._id || createdItem?.id || '',
        name: finalName,
        basePrice: finalPrice,
      };

      onItemCreated(result);
      onClose();
    } catch (err: any) {
      console.error('Erro ao cadastrar item:', err);
      toast.error(err.response?.data?.message || err.message || 'Erro ao cadastrar item no catálogo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200 border border-slate-100 my-8">
        {/* Header */}
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-2xl ${
              itemType === 'service' ? 'bg-blue-600' :
              itemType === 'product' ? 'bg-emerald-600' : 'bg-purple-600'
            }`}>
              {itemType === 'service' && <Wrench className="w-5 h-5 text-white" />}
              {itemType === 'product' && <Package className="w-5 h-5 text-white" />}
              {itemType === 'bundle' && <Layers className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="font-black text-lg leading-tight tracking-tight">Registo Rápido no Catálogo</h3>
              <p className="text-xs text-slate-300">Emita diretamente respeitando os parâmetros do sistema</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Selector */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex gap-2">
          <button
            type="button"
            onClick={() => setItemType('service')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-bold text-xs uppercase tracking-wider transition ${
              itemType === 'service'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Wrench className="w-4 h-4" />
            Serviço
          </button>

          <button
            type="button"
            onClick={() => setItemType('product')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-bold text-xs uppercase tracking-wider transition ${
              itemType === 'product'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Package className="w-4 h-4" />
            Produto
          </button>

          <button
            type="button"
            onClick={() => setItemType('bundle')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-bold text-xs uppercase tracking-wider transition ${
              itemType === 'bundle'
                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Layers className="w-4 h-4" />
            Combo / Subscrição
          </button>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          
          {/* ========================================================================= */}
          {/* 1. FORMULÁRIO DE SERVIÇO                                                 */}
          {/* ========================================================================= */}
          {itemType === 'service' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-100 rounded-2xl text-blue-800 text-xs font-medium">
                <Info className="w-4 h-4 text-blue-600 shrink-0" />
                <span>Cadastre serviços com suporte a unidades de tempo, parcelamento e juros acordados.</span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Nome do Serviço <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder="Ex: Consultoria em TI e Redes"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Preço Base (MT) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <DollarSign className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={serviceForm.basePrice || ''}
                      onChange={(e) => setServiceForm({ ...serviceForm, basePrice: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Unidade de Cobrança
                  </label>
                  <select
                    value={serviceForm.unit}
                    onChange={(e) => setServiceForm({ ...serviceForm, unit: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
                  >
                    {SERVICE_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Prestações Permitidas
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={serviceForm.allowedInstallments}
                    onChange={(e) => setServiceForm({ ...serviceForm, allowedInstallments: parseInt(e.target.value, 10) || 1 })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Nº máx de parcelas sem acréscimo</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Taxa Juros por Parcela Extra (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={serviceForm.penaltyPercentagePerInstallment}
                    onChange={(e) => setServiceForm({ ...serviceForm, penaltyPercentagePerInstallment: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">% adicionada quando o cliente pedir mais parcelas</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Público-Alvo ou Segmento (Opcional)
                </label>
                <input
                  type="text"
                  value={serviceForm.targetAudience}
                  onChange={(e) => setServiceForm({ ...serviceForm, targetAudience: e.target.value })}
                  placeholder="Ex: PMEs, Particulares, Startups"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Descrição do Serviço
                </label>
                <textarea
                  rows={2}
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  placeholder="Detalhes ou entregáveis inclusos no serviço..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none resize-none"
                />
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* 2. FORMULÁRIO DE PRODUTO                                                 */}
          {/* ========================================================================= */}
          {itemType === 'product' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-2xl text-emerald-800 text-xs font-medium">
                <Boxes className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Produtos com categorias setoriais, controlo de stock e preço de custo/venda.</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Nome do Produto <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    placeholder="Ex: Filtro de Óleo Bosch 450"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Categoria <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                  >
                    {PRODUCT_CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Preço de Venda (MT) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <DollarSign className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={productForm.basePrice || ''}
                      onChange={(e) => setProductForm({ ...productForm, basePrice: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Preço de Custo (MT)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={productForm.costPrice || ''}
                    onChange={(e) => setProductForm({ ...productForm, costPrice: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Unidade de Medida
                  </label>
                  <select
                    value={productForm.unit}
                    onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                  >
                    {PRODUCT_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Stock Inicial
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={productForm.stockQuantity}
                    onChange={(e) => setProductForm({ ...productForm, stockQuantity: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none font-semibold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Stock Mínimo (Alerta)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={productForm.minStockLevel}
                    onChange={(e) => setProductForm({ ...productForm, minStockLevel: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Código SKU (Opcional)
                  </label>
                  <input
                    type="text"
                    value={productForm.sku}
                    onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                    placeholder="Auto: PROD-..."
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Breve Descrição (máx 160 caracteres)
                </label>
                <input
                  type="text"
                  maxLength={160}
                  value={productForm.shortDescription}
                  onChange={(e) => setProductForm({ ...productForm, shortDescription: e.target.value })}
                  placeholder="Descrição rápida para faturas e catálogo..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none"
                />
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* 3. FORMULÁRIO DE COMBO / SUBSCRICÃO                                      */}
          {/* ========================================================================= */}
          {itemType === 'bundle' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              {/* Seletor Combo vs Subscrição */}
              <div className="grid grid-cols-2 gap-3 p-1.5 bg-slate-100 rounded-2xl">
                <button
                  type="button"
                  onClick={() => setBundleForm({ ...bundleForm, type: 'Combo' })}
                  className={`flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition ${
                    bundleForm.type === 'Combo'
                      ? 'bg-white text-purple-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Package className="w-4 h-4" />
                  Combo / Pacote Promocional
                </button>

                <button
                  type="button"
                  onClick={() => setBundleForm({ ...bundleForm, type: 'Subscription' })}
                  className={`flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition ${
                    bundleForm.type === 'Subscription'
                      ? 'bg-white text-purple-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  Subscrição Recorrente
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Nome do {bundleForm.type === 'Subscription' ? 'Plano / Subscrição' : 'Combo'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={bundleForm.name}
                  onChange={(e) => setBundleForm({ ...bundleForm, name: e.target.value })}
                  placeholder={
                    bundleForm.type === 'Subscription'
                      ? 'Ex: Plano Mensal de Suporte e Manutenção'
                      : 'Ex: Combo Instalação + 2 Câmaras HD'
                  }
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                />
              </div>

              {/* Campos específicos se for COMBO */}
              {bundleForm.type === 'Combo' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Preço Final (MT) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <DollarSign className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={bundleForm.price || ''}
                        onChange={(e) => setBundleForm({ ...bundleForm, price: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Preço Original Sem Desconto
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bundleForm.originalPrice || ''}
                      onChange={(e) => setBundleForm({ ...bundleForm, originalPrice: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Desconto Aplicado (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={bundleForm.discountPercentage || ''}
                      onChange={(e) => setBundleForm({ ...bundleForm, discountPercentage: parseFloat(e.target.value) || 0 })}
                      placeholder="Ex: 15"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Campos específicos se for SUBSCRIÇÃO */}
              {bundleForm.type === 'Subscription' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Ciclo de Faturação <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={bundleForm.billingCycle}
                        onChange={(e) => setBundleForm({ ...bundleForm, billingCycle: e.target.value })}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                      >
                        {BILLING_CYCLES.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Preço por Ciclo (MT) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <DollarSign className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          value={bundleForm.billingPricePerCycle || ''}
                          onChange={(e) => setBundleForm({ ...bundleForm, billingPricePerCycle: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                          className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Fidelização Mínima (Meses)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={bundleForm.commitmentMonths}
                        onChange={(e) => setBundleForm({ ...bundleForm, commitmentMonths: parseInt(e.target.value, 10) || 1 })}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Benefícios Incluídos (um por linha)
                    </label>
                    <textarea
                      rows={2}
                      value={bundleForm.extraBenefitsText}
                      onChange={(e) => setBundleForm({ ...bundleForm, extraBenefitsText: e.target.value })}
                      placeholder="Ex: Suporte prioritário 24/7&#10;Visita técnica mensal incluída"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none resize-none"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Descrição Geral
                </label>
                <textarea
                  rows={2}
                  value={bundleForm.description}
                  onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })}
                  placeholder="Detalhes ou condições gerais..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none resize-none"
                />
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-2xl transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center gap-2 px-7 py-2.5 text-white text-sm font-black uppercase tracking-wider rounded-2xl transition disabled:opacity-50 shadow-lg ${
                itemType === 'service' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20' :
                itemType === 'product' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20' :
                'bg-purple-600 hover:bg-purple-700 shadow-purple-600/20'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  A Registar...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Registar no Catálogo
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
